import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { boardApi } from "../api/client.js";
import { useBoard } from "../board/BoardContext.jsx";
import { useToast } from "../components/Toast.jsx";
import { ymd } from "../lib/domain.js";

/* Export, import and reset — the three writes that can lose another team's
 * work, so they are administrators only and each says what it is about to
 * do before it does it.
 *
 * Export first if a change touches the data model: that is what these are
 * for. An export reads tolerantly on the way back in, so a v1 export of
 * the original single-file build imports here unchanged. */
export function DataPage() {
  const { board, reload, replace } = useBoard();
  const toast = useToast();
  const file = useRef(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  async function exportAll() {
    setBusy(true);
    try {
      const data = await boardApi.exportAll();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "war-room-" + ymd(new Date()) + ".json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Exported " + data.playgrounds.length + " playgrounds");
    } catch (e) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  }

  function chooseFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const list = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed.playgrounds)
            ? parsed.playgrounds
            : null;
        if (!list?.length) {
          toast("That file holds no playgrounds", true);
          return;
        }
        setPending({ kind: "import", list, name: f.name });
      } catch {
        toast("That file is not readable JSON", true);
      }
    };
    reader.onerror = () => toast("That file could not be read", true);
    reader.readAsText(f);
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "import") {
        const { playgrounds } = await boardApi.import(pending.list);
        replace(playgrounds);
        toast("Imported " + playgrounds.length + " playgrounds");
      } else {
        const { playgrounds } = await boardApi.reset();
        replace(playgrounds);
        toast("Board reset to the seed scaffolds");
      }
      setPending(null);
      reload();
    } catch (e) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page narrow">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">Data</h2>
          <p className="pagesub">
            {board ? board.length + " playgrounds on the board" : "Reading the board…"}
          </p>
        </div>
        <Link className="btn btn-sm" to="/">
          Back to the board
        </Link>
      </div>

      {pending && (
        <div className="confirmbar">
          <span>
            {pending.kind === "import" ? (
              <>
                <b>{pending.name}</b> holds {pending.list.length} playgrounds. Importing
                replaces every playground on this board.
              </>
            ) : (
              <>
                This deletes all {board?.length ?? 0} playgrounds and writes the ten
                seed scaffolds back. Every KPI, player and task goes.
              </>
            )}
          </span>
          <button className="btn btn-sm btn-armed" type="button" disabled={busy} onClick={confirm}>
            {busy ? "Working…" : pending.kind === "import" ? "Replace the board" : "Reset it all"}
          </button>
          <button className="btn btn-sm" type="button" onClick={() => setPending(null)}>
            Cancel
          </button>
        </div>
      )}

      <div className="sechead">
        <span className="label">Export</span>
        <span className="rule" />
      </div>
      <p className="pagesub">
        A dated JSON file of every playground, with health, cadence, KPIs,
        players, tasks and templates. Do this before any change to the data
        model.
      </p>
      <button className="btn btn-solid" type="button" onClick={exportAll} disabled={busy}>
        Export the board
      </button>

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">Import</span>
        <span className="rule" />
      </div>
      <p className="pagesub">
        Replaces every playground with the file’s contents. A v1 export of the
        original single-file build reads correctly here.
      </p>
      <input
        ref={file}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={chooseFile}
      />
      <button className="btn" type="button" onClick={() => file.current?.click()} disabled={busy}>
        Choose a file…
      </button>

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">Reset</span>
        <span className="rule" />
      </div>
      <p className="pagesub">
        Back to the ten seed scaffolds — names, owners, cadences and the
        cross-playground notes, and nothing else. On a fresh board all ten read
        critical, because nothing has been reviewed. That is the true state,
        not a fault.
      </p>
      <button
        className="btn"
        type="button"
        onClick={() => setPending({ kind: "reset" })}
        disabled={busy}
      >
        Reset all data
      </button>
    </div>
  );
}
